import React, { Component } from 'react';
import { observable, action, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FeatherIcon from 'feather-icons-react';
import {
    Button,
    FormInput,
    FormSelect,
    FormCheckbox,
    Badge,
    Modal,
    ModalHeader,
    ModalBody
} from 'shards-react';

import './style.scss';
import PageTitle from '../../../components/common/PageTitle';
import ModalLoading from '../../../components/common/ModalLoading';

// 속성 수정(PUT) 시 보낼 수 있는 편집 가능 필드 (key/name 외)
const EDITABLE_FIELDS = ['display', 'display_name', 'font_type', 'font_size', 'font_color'];

const FONT_TYPE_OPTIONS = [
    { value: 'N', label: '기본' },
    { value: 'B', label: '굵게' },
    { value: 'I', label: '기울임' },
    { value: 'BI', label: '굵게+기울임' },
];

// 새 사용자 속성 기본값 (cafe24 POST 예시 기준)
const NEW_PROPERTY_INIT = {
    name: '',
    display: 'T',
    display_name: 'T',
    font_type: 'N',
    font_size: 13,
    font_color: '#000000',
};

@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ design }) => ({ getMainDisplayGroup: design.getMainDisplayGroup }))
@inject(({ product }) => ({ getProductList: product.getProductList }))
@inject(({ booster }) => ({
    getCafe24MainProperties: booster.getCafe24MainProperties,
    addCafe24MainProperty: booster.addCafe24MainProperty,
    updateCafe24MainProperties: booster.updateCafe24MainProperties,
    getCafe24MainProducts: booster.getCafe24MainProducts,
    addCafe24MainProducts: booster.addCafe24MainProducts,
    updateCafe24MainProducts: booster.updateCafe24MainProducts,
    deleteCafe24MainProduct: booster.deleteCafe24MainProduct,
    addCafe24MainDisplayGroup: booster.addCafe24MainDisplayGroup,
}))
@observer
class MainDisplay extends Component {

    // basic
    @observable store = {};
    @observable teamUser = {};
    @observable isLogin = false;
    @observable isLoading = false;

    // 진열 그룹
    @observable groups = [];
    @observable selectedGroup = null; // display_group 번호
    @observable groupsError = '';
    @observable groupsLoaded = false;

    // ===== 진열 상품 (페이지 본문) =====
    @observable dpItems = [];         // [{ product_no, product_name, isFixed, raw }]
    dpOriginal = '';                  // 순서+고정 스냅샷 (JSON)
    @observable dpLoaded = false;
    @observable dpError = '';

    // ===== 진열 속성 (표시 설정 모달) =====
    @observable properties = [];      // 현재 그룹 속성 (원본)
    originalMap = {};                 // key → 원본 row
    @observable editedRows = {};      // key → { field: value } (변경분만)
    @observable propsError = '';

    // 모달
    @observable modals = { isProps: false, isAddProducts: false, isAddGroup: false };
    @observable newGroupName = '';
    @observable showAddPropertyForm = false;   // 표시 설정 모달 내 인라인 추가 폼
    @observable newProperty = { ...NEW_PROPERTY_INIT };

    // 상품 추가 모달 — 검색
    @observable productSearch = { keyword: '', results: [], isSearching: false, searched: false };
    @observable selectedProductNos = {};       // platformProductId(string) → true

    constructor(props) {
        super(props);
        this.init();
    }

    @action
    init = async () => {
        if (this.props.isLogin) {
            this.isLogin = this.props.isLogin;
            this.teamUser = this.props.me;
            this.store = this.props.me.store ? this.props.me.store : {};
            this.fetchGroups();
        }
    };

    baseParams = () => ({
        store_id: this.teamUser.selStore_id,
        shopNo: this.teamUser.selShopNo || 1,
    });

    // cafe24 프록시 응답 공통 에러 추출 (GraphQL 에러 / cafe24 에러)
    extractError = (res) => {
        let msg = null;
        if (typeof res === 'string') {
            // 리졸버/Cafe24.setProperties 가 에러를 문자열(e.message)로 반환한 경우
            msg = res;
        }
        else if (res instanceof Error) {
            msg = res.message;
        }
        else if (res && res.errors && res.errors.length > 0) {
            msg = res.errors[0].message || '요청에 실패했습니다.';
        }
        else {
            const raw = res && res.data !== undefined ? res.data : null;
            if (typeof raw === 'string') {
                // res.data 가 에러 문자열인 경우(카페24 호출 실패 시 setProperties catch 반환).
                // 과거엔 객체가 아니라 무시되어 실패해도 "저장되었습니다"로 표시되던 문제.
                msg = raw;
            }
            else if (raw && raw.error) {
                msg = raw.error.message || '카페24 응답 오류가 발생했습니다.';
                if (raw.error.code) msg += ` (${raw.error.code})`;
            }
        }
        // 카페24 앱 권한(scope) 부족 — 앱 재인증 안내
        if (msg && (msg.indexOf('insufficient_scope') > -1 || msg.indexOf('permission necessary') > -1)) {
            return '카페24 앱에 메인진열 변경 권한이 없습니다. 카페24 관리자에서 온리플 앱을 다시 실행해 새 권한(카테고리/진열 쓰기)에 동의하면 사용할 수 있습니다.';
        }
        return msg;
    };

    // ===== 진열 그룹 =====
    // preferGroup: 재조회 후 우선 선택할 display_group (그룹 추가 직후 새 그룹 자동 선택용)
    @action
    fetchGroups = (preferGroup) => {
        let _this = this;
        this.isLoading = true;
        this.groupsError = '';

        this.props.getMainDisplayGroup(this.baseParams(), action(function (res) {
            _this.groupsLoaded = true;
            const err = _this.extractError(res);
            if (err) {
                _this.isLoading = false;
                _this.groupsError = err;
                return;
            }
            const raw = res && res.data ? res.data : {};
            _this.groups = (raw && raw.mains) ? raw.mains : [];
            if (_this.groups.length > 0) {
                const target = preferGroup && _this.groups.some(g => g.display_group === preferGroup)
                    ? preferGroup
                    : _this.groups[0].display_group;
                _this.fetchGroupData(target);
            } else {
                _this.isLoading = false;
            }
        }));
    };

    // 그룹 선택 시 진열 상품 + 표시 속성 동시 로드
    @action
    fetchGroupData = (displayGroup) => {
        this.selectedGroup = displayGroup;
        this.fetchMainProducts(displayGroup);
        this.fetchProperties(displayGroup);
    };

    @action
    selectGroup = (displayGroup) => {
        if (this.selectedGroup === displayGroup) return;
        if (this.dpDirty || this.dirtyKeys.length > 0) {
            if (!window.confirm('저장되지 않은 변경사항이 있습니다. 이동하시겠습니까?')) return;
        }
        this.fetchGroupData(displayGroup);
    };

    // ===== 진열 상품 =====
    @action
    fetchMainProducts = (displayGroup) => {
        let _this = this;
        this.isLoading = true;
        this.dpError = '';

        this.props.getCafe24MainProducts({
            ...this.baseParams(),
            displayGroup: displayGroup,
        }, action(function (res) {
            _this.isLoading = false;
            _this.dpLoaded = true;
            const err = _this.extractError(res);
            if (err) {
                _this.dpError = err;
                _this.dpItems = [];
                _this.dpOriginal = _this.dpSnapshot(); // 빈 상태 기준으로 동기화 (가짜 dirty 방지)
                return;
            }
            const raw = res && res.data ? res.data : {};
            const list = (raw && raw.products) ? raw.products : [];
            _this.dpItems = list.map(p => ({
                product_no: p.product_no,
                product_name: p.product_name || '',
                image: '',   // 메인진열 API엔 이미지가 없어 별도 조회로 보강
                // 고정 여부 필드는 응답 형태에 따라 방어적으로 해석
                isFixed: p.fix_product_no === 'T' || p.fix_product === 'T' || p.fixed === 'T' || p.fixed === true,
                raw: p,
            }));
            _this.dpOriginal = _this.dpSnapshot();
            _this.fetchProductImages();
        }));
    };

    // 메인진열 API 응답엔 상품 이미지가 없어, 온리플 상품 DB에서 product_no(platformProductId)로 이미지를 보강한다
    @action
    fetchProductImages = () => {
        let _this = this;
        const nos = this.dpItems.map(it => String(it.product_no)).filter(Boolean);
        if (nos.length === 0) return;
        this.props.getProductList({
            ...this.baseParams(),
            limit: nos.length,
            page: 1,
            searchParams: { platformProductIds: nos },
        }, action(function (res) {
            if (!res || !res.docs) return;
            const imgMap = {};
            res.docs.forEach(p => {
                const img = p.image || (p.images && (p.images.s_1 || p.images.m_1)) || '';
                if (p.platformProductId) imgMap[String(p.platformProductId)] = img;
            });
            // 현재 dpItems 기준으로 image 만 채워 재할당 (순서/고정 변경과 충돌 없음, dirty 스냅샷에도 영향 없음)
            _this.dpItems = _this.dpItems.map(it => ({
                ...it,
                image: imgMap[String(it.product_no)] || it.image || '',
            }));
        }));
    };

    dpSnapshot = () => JSON.stringify(this.dpItems.map(it => ({ n: it.product_no, f: !!it.isFixed })));

    @computed
    get dpDirty() {
        if (!this.dpLoaded) return false;
        return this.dpSnapshot() !== this.dpOriginal;
    }

    @computed
    get dpFixedCount() {
        return this.dpItems.filter(it => it.isFixed).length;
    }

    // 순서 이동 (▲▼)
    @action
    moveProduct = (idx, dir) => {
        const to = idx + dir;
        if (to < 0 || to >= this.dpItems.length) return;
        const arr = this.dpItems.slice();
        const tmp = arr[idx];
        arr[idx] = arr[to];
        arr[to] = tmp;
        this.dpItems = arr;
    };

    @action
    toggleFixed = (idx) => {
        const arr = this.dpItems.slice();
        arr[idx] = { ...arr[idx], isFixed: !arr[idx].isFixed };
        this.dpItems = arr;
    };

    @action
    revertProducts = () => {
        if (!this.selectedGroup) return;
        this.fetchMainProducts(this.selectedGroup);
    };

    // 순서/고정 저장 (PUT — 전체 순서 배열 + 고정 배열)
    @action
    saveProducts = () => {
        let _this = this;
        if (!this.dpDirty) return;

        this.isLoading = true;
        this.props.updateCafe24MainProducts({
            ...this.baseParams(),
            displayGroup: this.selectedGroup,
            productNos: this.dpItems.map(it => it.product_no),
            fixProductNos: this.dpItems.filter(it => it.isFixed).map(it => it.product_no),
        }, action(function (res) {
            _this.isLoading = false;
            const err = _this.extractError(res);
            if (err) {
                toast.error(err);
                return;
            }
            toast.success('진열 상품이 저장되었습니다.');
            _this.fetchMainProducts(_this.selectedGroup);
        }));
    };

    // 진열 상품 삭제 (DELETE — 단건 즉시)
    @action
    removeProduct = (item) => {
        let _this = this;
        let msg = `[${item.product_name || '상품번호 ' + item.product_no}] 을(를) 진열에서 삭제하시겠습니까?`;
        if (this.dpDirty) {
            msg += '\n(저장되지 않은 순서/고정 변경은 초기화됩니다)';
        }
        if (!window.confirm(msg)) return;

        this.isLoading = true;
        this.props.deleteCafe24MainProduct({
            ...this.baseParams(),
            displayGroup: this.selectedGroup,
            productNo: item.product_no,
        }, action(function (res) {
            _this.isLoading = false;
            const err = _this.extractError(res);
            if (err) {
                toast.error(err);
                return;
            }
            toast.success('진열에서 삭제되었습니다.');
            _this.fetchMainProducts(_this.selectedGroup);
        }));
    };

    // ===== 진열 그룹 추가 =====
    @action
    openAddGroupModal = () => {
        this.newGroupName = '';
        this.modals.isAddGroup = true;
    };

    @action
    submitAddGroup = () => {
        let _this = this;
        const name = (this.newGroupName || '').trim();
        if (!name) {
            toast('그룹 이름을 입력해 주세요.');
            return;
        }

        this.isLoading = true;
        this.props.addCafe24MainDisplayGroup({
            ...this.baseParams(),
            groupName: name,
        }, action(function (res) {
            _this.isLoading = false;
            const err = _this.extractError(res);
            if (err) {
                toast.error(err);
                return;
            }
            toast.success('진열 그룹이 추가되었습니다.');
            _this.modals.isAddGroup = false;
            // 응답의 새 display_group 번호로 자동 선택 (응답: { main: { display_group, ... } })
            const raw = res && res.data ? res.data : {};
            const newNo = raw && raw.main && raw.main.display_group ? raw.main.display_group : null;
            _this.fetchGroups(newNo);
        }));
    };

    // ===== 상품 추가 모달 =====
    @action
    openAddProductsModal = () => {
        this.productSearch = { keyword: '', results: [], isSearching: false, searched: false };
        this.selectedProductNos = {};
        this.modals.isAddProducts = true;
    };

    @action
    searchProducts = () => {
        let _this = this;
        const keyword = (this.productSearch.keyword || '').trim();
        this.productSearch.isSearching = true;

        this.props.getProductList({
            ...this.baseParams(),
            limit: 20,
            page: 1,
            searchParams: keyword ? { keyword: keyword } : {},
        }, action(function (res) {
            _this.productSearch.isSearching = false;
            _this.productSearch.searched = true;
            if (res && res.docs) {
                _this.productSearch.results = res.docs;
            } else {
                _this.productSearch.results = [];
            }
        }));
    };

    onSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.searchProducts();
        }
    };

    @action
    toggleSelectProduct = (platformProductId) => {
        const next = { ...this.selectedProductNos };
        if (next[platformProductId]) {
            delete next[platformProductId];
        } else {
            next[platformProductId] = true;
        }
        this.selectedProductNos = next;
    };

    @computed
    get selectedCount() {
        return Object.keys(this.selectedProductNos).length;
    }

    // 이미 진열 중인 상품번호 셋
    @computed
    get displayedNoSet() {
        return this.dpItems.reduce((acc, it) => { acc[String(it.product_no)] = true; return acc; }, {});
    }

    @action
    submitAddProducts = () => {
        let _this = this;
        const nos = Object.keys(this.selectedProductNos).map(n => parseInt(n, 10)).filter(n => !isNaN(n));
        if (nos.length === 0) {
            toast('추가할 상품을 선택해 주세요.');
            return;
        }

        this.isLoading = true;
        this.props.addCafe24MainProducts({
            ...this.baseParams(),
            displayGroup: this.selectedGroup,
            productNos: nos,
        }, action(function (res) {
            _this.isLoading = false;
            const err = _this.extractError(res);
            if (err) {
                toast.error(err);
                return;
            }
            toast.success(`${nos.length}개 상품이 진열에 추가되었습니다.`);
            _this.modals.isAddProducts = false;
            _this.fetchMainProducts(_this.selectedGroup);
        }));
    };

    // ===== 진열 속성 (표시 설정 모달) =====
    @action
    fetchProperties = (displayGroup) => {
        let _this = this;
        this.propsError = '';
        this.editedRows = {};

        this.props.getCafe24MainProperties({
            ...this.baseParams(),
            displayGroup: displayGroup,
        }, action(function (res) {
            const err = _this.extractError(res);
            if (err) {
                _this.propsError = err;
                _this.properties = [];
                return;
            }
            const raw = res && res.data ? res.data : {};
            const main = raw && raw.main ? raw.main : raw;
            const list = (main && main.properties) ? main.properties : [];
            _this.properties = list;
            _this.originalMap = list.reduce((acc, p) => { acc[p.key] = p; return acc; }, {});
        }));
    };

    @action
    openPropsModal = () => {
        this.showAddPropertyForm = false;
        this.newProperty = { ...NEW_PROPERTY_INIT };
        this.modals.isProps = true;
    };

    @action
    closePropsModal = () => {
        if (this.dirtyKeys.length > 0) {
            if (!window.confirm('저장되지 않은 설정 변경이 있습니다. 닫으시겠습니까?')) return;
            this.editedRows = {};
        }
        this.modals.isProps = false;
    };

    // 셀 값 변경 — 원본과 같아지면 변경분에서 제거
    @action
    setField = (key, field, value) => {
        const orig = this.originalMap[key] || {};
        const cur = this.editedRows[key] ? { ...this.editedRows[key] } : {};
        const origVal = orig[field];

        if (origVal !== undefined && String(origVal) === String(value)) {
            delete cur[field];
        } else {
            cur[field] = value;
        }

        const next = { ...this.editedRows };
        if (Object.keys(cur).length > 0) {
            next[key] = cur;
        } else {
            delete next[key];
        }
        this.editedRows = next;
    };

    fieldValue = (row, field, defaultVal) => {
        const edited = this.editedRows[row.key];
        if (edited && edited[field] !== undefined) return edited[field];
        if (row[field] !== undefined && row[field] !== null) return row[field];
        return defaultVal;
    };

    @computed
    get dirtyKeys() {
        return Object.keys(this.editedRows);
    }

    @action
    revertProperties = () => {
        this.editedRows = {};
    };

    // 변경된 속성만 PUT — 원본에 있던 편집 필드 + 변경분 병합
    @action
    saveProperties = () => {
        let _this = this;
        const keys = this.dirtyKeys;
        if (keys.length === 0) return;

        const payload = keys.map(key => {
            const orig = this.originalMap[key] || {};
            const row = { key: key, name: orig.name };
            EDITABLE_FIELDS.forEach(f => {
                if (orig[f] !== undefined && orig[f] !== null) row[f] = orig[f];
            });
            Object.assign(row, this.editedRows[key]);
            if (row.font_size !== undefined) row.font_size = parseInt(row.font_size, 10);
            return row;
        });

        this.isLoading = true;
        this.props.updateCafe24MainProperties({
            ...this.baseParams(),
            displayGroup: this.selectedGroup,
            properties: payload,
        }, action(function (res) {
            _this.isLoading = false;
            const err = _this.extractError(res);
            if (err) {
                toast.error(err);
                return;
            }
            toast.success('표시 설정이 저장되었습니다.');
            _this.fetchProperties(_this.selectedGroup);
        }));
    };

    @action
    submitAddProperty = () => {
        let _this = this;
        const np = this.newProperty;
        if (!np.name || np.name.trim() === '') {
            toast('속성 이름을 입력해 주세요.');
            return;
        }

        const property = {
            multishop_display_names: [
                { shop_no: this.teamUser.selShopNo || 1, name: np.name.trim() }
            ],
            display: np.display,
            display_name: np.display_name,
            font_type: np.font_type,
            font_size: parseInt(np.font_size, 10) || 13,
            font_color: np.font_color,
            exposure_group_type: 'M',
        };

        this.isLoading = true;
        this.props.addCafe24MainProperty({
            ...this.baseParams(),
            property: property,
        }, action(function (res) {
            _this.isLoading = false;
            const err = _this.extractError(res);
            if (err) {
                toast.error(err);
                return;
            }
            toast.success('사용자 속성이 추가되었습니다.');
            _this.showAddPropertyForm = false;
            _this.newProperty = { ...NEW_PROPERTY_INIT };
            if (_this.selectedGroup) {
                _this.fetchProperties(_this.selectedGroup);
            }
        }));
    };

    // T/F 토글 렌더 (속성 모달)
    renderTFToggle = (row, field, defaultVal) => {
        const val = this.fieldValue(row, field, defaultVal);
        return (
            <FormCheckbox
                toggle small
                checked={val === 'T'}
                onChange={() => this.setField(row.key, field, val === 'T' ? 'F' : 'T')}>
                <span className={`md-tf ${val === 'T' ? 'md-tf-on' : ''}`}>{val === 'T' ? '노출' : '숨김'}</span>
            </FormCheckbox>
        );
    };

    render() {
        const groupName = (g) => g.group_name || g.name || `진열 그룹 ${g.display_group}`;
        const curGroup = this.groups.find(g => g.display_group === this.selectedGroup);

        return (
            <div className="main-content main-display-page">
                <PageTitle />

                {(this.isLogin && this.teamUser.emailId) ? (
                    <div className="md-container">

                        {/* 안내 */}
                        <div className="md-guide">
                            <FeatherIcon icon="info" size="14" aria-hidden="true" />
                            <span>
                                카페24 쇼핑몰 <strong>메인 진열 영역(추천상품, 신상품 등)</strong>에 등록된 상품을 관리합니다.
                                상품 추가/삭제, 진열 순서 변경, 상품 고정과 표시 설정(글꼴·노출 항목)을 지원하며 저장 시 쇼핑몰에 바로 반영됩니다.
                            </span>
                        </div>

                        {/* 그룹 선택 + 액션 */}
                        <div className="md-toolbar">
                            <div className="md-toolbar-left">
                                <span className="md-toolbar-title">
                                    <FeatherIcon icon="grid" size="13" aria-hidden="true" /> 진열 그룹
                                </span>
                                <div className="md-groups" role="radiogroup" aria-label="메인 진열 그룹 선택">
                                    {this.groups.map(g => (
                                        <button key={g.display_group} type="button"
                                                role="radio"
                                                aria-checked={this.selectedGroup === g.display_group}
                                                className={`md-group-chip ${this.selectedGroup === g.display_group ? 'is-active' : ''}`}
                                                onClick={() => this.selectGroup(g.display_group)}>
                                            {groupName(g)}
                                            <span className="md-group-no">#{g.display_group}</span>
                                        </button>
                                    ))}
                                    {this.groups.length === 0 && this.groupsLoaded && !this.groupsError && (
                                        <span className="md-muted">진열 그룹이 없습니다</span>
                                    )}
                                    <button type="button" className="md-group-add-chip"
                                            onClick={() => this.openAddGroupModal()}
                                            title="새 진열 그룹을 추가합니다">
                                        <FeatherIcon icon="plus" size="11" aria-hidden="true" /> 그룹 추가
                                    </button>
                                </div>
                            </div>
                            <div className="md-toolbar-right">
                                <Button theme="light" size="sm"
                                        onClick={() => this.selectedGroup ? this.fetchGroupData(this.selectedGroup) : this.fetchGroups()}>
                                    <FeatherIcon icon="refresh-cw" size="12" aria-hidden="true" /> 새로고침
                                </Button>
                                <Button theme="light" size="sm"
                                        disabled={!this.selectedGroup}
                                        onClick={() => this.openPropsModal()}>
                                    <FeatherIcon icon="sliders" size="12" aria-hidden="true" /> 표시 설정
                                </Button>
                                <Button theme="primary" size="sm"
                                        disabled={!this.selectedGroup}
                                        onClick={() => this.openAddProductsModal()}>
                                    <FeatherIcon icon="plus" size="12" aria-hidden="true" /> 상품 추가
                                </Button>
                            </div>
                        </div>

                        {/* 진열 상품 헤더 요약 */}
                        {this.dpLoaded && !this.dpError && this.selectedGroup && (
                            <div className="md-dp-summary">
                                <strong>{curGroup ? groupName(curGroup) : ''}</strong> 진열 상품
                                <Badge theme="light" className="md-dp-badge">{this.dpItems.length}개</Badge>
                                {this.dpFixedCount > 0 && (
                                    <Badge theme="warning" className="md-dp-badge">고정 {this.dpFixedCount}개</Badge>
                                )}
                                <span className="md-dp-hint">
                                    <FeatherIcon icon="move" size="11" aria-hidden="true" /> ▲▼ 버튼으로 순서를 바꾸고, 고정 토글로 상품을 해당 순서에 고정할 수 있습니다
                                </span>
                            </div>
                        )}

                        {/* 진열 상품 테이블 */}
                        {this.dpLoaded && !this.dpError && this.dpItems.length > 0 && (
                            <div className="md-table-wrap">
                                <table className="md-table">
                                    <colgroup>
                                        <col width="70" />
                                        <col width="110" />
                                        <col />
                                        <col width="110" />
                                        <col width="110" />
                                        <col width="80" />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th className="text-center">순서</th>
                                            <th>상품번호</th>
                                            <th>상품명</th>
                                            <th className="text-center">고정</th>
                                            <th className="text-center">순서 이동</th>
                                            <th className="text-center">삭제</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.dpItems.map((item, idx) => (
                                            <tr key={`${item.product_no}_${idx}`} className={item.isFixed ? 'is-fixed' : ''}>
                                                <td className="text-center">
                                                    <span className="md-order-no">{idx + 1}</span>
                                                </td>
                                                <td>
                                                    <span className="md-prod-no">{item.product_no}</span>
                                                </td>
                                                <td>
                                                    <div className="md-prod-cell">
                                                        <div className="md-prod-thumb">
                                                            {item.image
                                                                ? <img src={item.image} alt="" loading="lazy" />
                                                                : <span className="md-prod-thumb-empty"><FeatherIcon icon="image" size="15" aria-hidden="true" /></span>}
                                                        </div>
                                                        <div className="md-prod-name">
                                                            {item.isFixed && (
                                                                <Badge theme="warning" className="md-fix-badge">
                                                                    <FeatherIcon icon="lock" size="9" aria-hidden="true" /> 고정
                                                                </Badge>
                                                            )}
                                                            <span>{item.product_name || <span className="md-muted">(상품명 정보 없음)</span>}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <FormCheckbox
                                                        toggle small
                                                        checked={!!item.isFixed}
                                                        onChange={() => this.toggleFixed(idx)}>
                                                        <span className={`md-tf ${item.isFixed ? 'md-tf-on' : ''}`}>{item.isFixed ? 'ON' : 'OFF'}</span>
                                                    </FormCheckbox>
                                                </td>
                                                <td className="text-center">
                                                    <div className="md-move-btns">
                                                        <button type="button" className="md-move-btn"
                                                                disabled={idx === 0}
                                                                onClick={() => this.moveProduct(idx, -1)}
                                                                aria-label={`${item.product_name || item.product_no} 위로 이동`}>
                                                            <FeatherIcon icon="chevron-up" size="14" aria-hidden="true" />
                                                        </button>
                                                        <button type="button" className="md-move-btn"
                                                                disabled={idx === this.dpItems.length - 1}
                                                                onClick={() => this.moveProduct(idx, 1)}
                                                                aria-label={`${item.product_name || item.product_no} 아래로 이동`}>
                                                            <FeatherIcon icon="chevron-down" size="14" aria-hidden="true" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <button type="button" className="md-del-btn"
                                                            onClick={() => this.removeProduct(item)}
                                                            aria-label={`${item.product_name || item.product_no} 진열에서 삭제`}>
                                                        <FeatherIcon icon="trash-2" size="13" aria-hidden="true" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* 빈 상태 */}
                        {this.dpLoaded && !this.dpError && this.selectedGroup && this.dpItems.length === 0 && (
                            <div className="md-empty">
                                <FeatherIcon icon="inbox" size="40" aria-hidden="true" />
                                <p className="md-empty-title">이 그룹에 진열된 상품이 없습니다</p>
                                <p className="md-empty-desc">상품을 추가해 메인 진열을 구성해 보세요.</p>
                                <Button theme="primary" size="sm" onClick={() => this.openAddProductsModal()}>
                                    <FeatherIcon icon="plus" size="12" aria-hidden="true" /> 상품 추가
                                </Button>
                            </div>
                        )}

                        {/* 에러 상태 */}
                        {(this.groupsError || this.dpError) && (
                            <div className="md-empty md-error">
                                <FeatherIcon icon="alert-triangle" size="40" aria-hidden="true" />
                                <p className="md-empty-title">불러오지 못했습니다</p>
                                <p className="md-empty-desc">{this.groupsError || this.dpError}</p>
                                <Button theme="primary" size="sm" onClick={() => this.fetchGroups()}>다시 시도</Button>
                            </div>
                        )}

                        {/* Sticky 저장 바 — 진열 상품 순서/고정 변경 (위젯/알림톡 설정과 동일 패턴) */}
                        {this.dpDirty && (
                            <div className="wbp-sticky-save wbp-sticky-save--top" role="status" aria-live="polite">
                                <div className="wbp-sticky-save__msg">
                                    <FeatherIcon icon="alert-circle" size="16" />
                                    <span><strong>진열 순서/고정</strong>에 저장되지 않은 변경사항이 있습니다.</span>
                                </div>
                                <div className="wbp-sticky-save__actions">
                                    <Button theme="light" size="sm" onClick={() => this.revertProducts()}>되돌리기</Button>
                                    <Button theme="primary" size="sm" onClick={() => this.saveProducts()}>변경사항 저장</Button>
                                </div>
                            </div>
                        )}

                    </div>
                ) : null}

                {/* ===== 진열 그룹 추가 모달 ===== */}
                <Modal size="sm" className="modal-text main-display-page" open={this.modals.isAddGroup}
                       toggle={() => this.modals.isAddGroup = false} centered={true}>
                    <ModalHeader toggle={() => this.modals.isAddGroup = false}>진열 그룹 추가</ModalHeader>
                    <ModalBody>
                        <div className="md-add-form">
                            <p className="md-add-desc">
                                카페24 쇼핑몰에 새 메인 진열 그룹을 추가합니다. 추가 후 상품을 등록해 진열을 구성할 수 있습니다.
                            </p>
                            <div className="md-add-row">
                                <label htmlFor="md-new-group-name">그룹 이름 <span className="md-required">*</span></label>
                                <FormInput id="md-new-group-name"
                                           placeholder="예: 이달의 인기상품"
                                           value={this.newGroupName}
                                           onChange={(e) => this.newGroupName = e.target.value}
                                           onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.submitAddGroup(); } }} />
                            </div>
                            <div className="text-center" style={{ marginTop: '18px' }}>
                                <Button theme="light" onClick={() => this.modals.isAddGroup = false}>취소</Button>
                                <Button className="ml-2" theme="primary" onClick={() => this.submitAddGroup()}>
                                    <FeatherIcon icon="plus" size="13" style={{ marginRight: '4px' }} aria-hidden="true" />
                                    추가하기
                                </Button>
                            </div>
                        </div>
                    </ModalBody>
                </Modal>

                {/* ===== 상품 추가 모달 ===== */}
                <Modal size="lg" className="modal-text main-display-page" open={this.modals.isAddProducts}
                       toggle={() => this.modals.isAddProducts = false} centered={true}>
                    <ModalHeader toggle={() => this.modals.isAddProducts = false}>
                        진열 상품 추가 {curGroup ? `— ${groupName(curGroup)}` : ''}
                    </ModalHeader>
                    <ModalBody>
                        <div className="md-add-products">
                            <div className="md-search-row">
                                <FormInput
                                    placeholder="상품명으로 검색 (비우면 전체 조회)"
                                    value={this.productSearch.keyword}
                                    onChange={(e) => this.productSearch.keyword = e.target.value}
                                    onKeyDown={this.onSearchKeyDown} />
                                <Button theme="primary" onClick={() => this.searchProducts()} disabled={this.productSearch.isSearching}>
                                    <FeatherIcon icon="search" size="13" aria-hidden="true" /> 검색
                                </Button>
                            </div>

                            {this.productSearch.isSearching && (
                                <p className="md-muted md-search-state">검색 중...</p>
                            )}

                            {!this.productSearch.isSearching && this.productSearch.searched && this.productSearch.results.length === 0 && (
                                <p className="md-muted md-search-state">검색 결과가 없습니다.</p>
                            )}

                            {!this.productSearch.isSearching && this.productSearch.results.length > 0 && (
                                <div className="md-search-results">
                                    {this.productSearch.results.map(p => {
                                        const no = String(p.platformProductId);
                                        const already = !!this.displayedNoSet[no];
                                        const checked = !!this.selectedProductNos[no];
                                        const img = (p.images && p.images.s_1) ? p.images.s_1 : p.image;
                                        return (
                                            <div key={p._id}
                                                 className={`md-search-item ${already ? 'is-disabled' : ''} ${checked ? 'is-checked' : ''}`}
                                                 onClick={() => { if (!already) this.toggleSelectProduct(no); }}
                                                 role="checkbox"
                                                 aria-checked={checked}
                                                 aria-disabled={already}
                                                 tabIndex={already ? -1 : 0}
                                                 onKeyDown={(e) => {
                                                     if (!already && (e.key === 'Enter' || e.key === ' ')) {
                                                         e.preventDefault();
                                                         this.toggleSelectProduct(no);
                                                     }
                                                 }}>
                                                <div className="md-search-check">
                                                    <FeatherIcon icon={checked ? 'check-square' : 'square'} size="16" aria-hidden="true" />
                                                </div>
                                                {img ? (
                                                    <img className="md-search-img" src={img} alt={p.productName} />
                                                ) : (
                                                    <div className="md-search-img md-search-img-empty">
                                                        <FeatherIcon icon="image" size="14" aria-hidden="true" />
                                                    </div>
                                                )}
                                                <div className="md-search-info">
                                                    <div className="md-search-name" title={p.productName}>{p.productName}</div>
                                                    <div className="md-search-meta">상품번호 {no}</div>
                                                </div>
                                                {already && <Badge theme="light" className="md-search-badge">진열중</Badge>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="md-add-footer">
                                <span className="md-muted">{this.selectedCount > 0 ? `${this.selectedCount}개 선택됨` : '추가할 상품을 선택하세요'}</span>
                                <div>
                                    <Button theme="light" onClick={() => this.modals.isAddProducts = false}>취소</Button>
                                    <Button className="ml-2" theme="primary"
                                            disabled={this.selectedCount === 0}
                                            onClick={() => this.submitAddProducts()}>
                                        <FeatherIcon icon="plus" size="13" style={{ marginRight: '4px' }} aria-hidden="true" />
                                        {this.selectedCount > 0 ? `${this.selectedCount}개 추가하기` : '추가하기'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                </Modal>

                {/* ===== 표시 설정 모달 (진열 속성) ===== */}
                <Modal size="lg" className="modal-text main-display-page md-props-dialog" open={this.modals.isProps}
                       toggle={() => this.closePropsModal()} centered={true}>
                    <ModalHeader toggle={() => this.closePropsModal()}>
                        표시 설정 {curGroup ? `— ${groupName(curGroup)}` : ''}
                    </ModalHeader>
                    <ModalBody>
                        <div className="md-props-modal">
                            <p className="md-add-desc">
                                메인 진열 상품 아래에 표시되는 정보 항목(상품명, 판매가 등)의 노출 여부와 글꼴을 설정합니다.
                            </p>

                            {this.propsError && (
                                <div className="md-props-error">
                                    <FeatherIcon icon="alert-triangle" size="13" aria-hidden="true" />
                                    <span>{this.propsError}</span>
                                    <Button theme="light" size="sm" onClick={() => this.fetchProperties(this.selectedGroup)}>다시 시도</Button>
                                </div>
                            )}

                            {!this.propsError && this.properties.length > 0 && (
                                <div className="md-table-wrap md-props-table">
                                    <table className="md-table">
                                        <colgroup>
                                            <col />
                                            <col width="118" />
                                            <col width="124" />
                                            <col width="120" />
                                            <col width="72" />
                                            <col width="112" />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>항목</th>
                                                <th>노출</th>
                                                <th>항목명 노출</th>
                                                <th>글꼴</th>
                                                <th>크기</th>
                                                <th>색상</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.properties.map(row => {
                                                const isDirty = !!this.editedRows[row.key];
                                                return (
                                                    <tr key={row.key} className={isDirty ? 'is-dirty' : ''}>
                                                        <td>
                                                            <div className="md-prop">
                                                                <span className="md-prop-name">
                                                                    {row.name}
                                                                    {isDirty && <span className="md-dirty-dot" title="변경됨" aria-label="변경됨" />}
                                                                </span>
                                                                <span className="md-prop-key">{row.key}</span>
                                                            </div>
                                                        </td>
                                                        <td>{this.renderTFToggle(row, 'display', 'F')}</td>
                                                        <td>{this.renderTFToggle(row, 'display_name', 'T')}</td>
                                                        <td>
                                                            <FormSelect size="sm"
                                                                        value={this.fieldValue(row, 'font_type', 'N')}
                                                                        onChange={(e) => this.setField(row.key, 'font_type', e.target.value)}>
                                                                {FONT_TYPE_OPTIONS.map(o => (
                                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                                ))}
                                                            </FormSelect>
                                                        </td>
                                                        <td>
                                                            <FormInput type="number" size="sm" min="8" max="40"
                                                                       value={this.fieldValue(row, 'font_size', 13)}
                                                                       onChange={(e) => this.setField(row.key, 'font_size', e.target.value)} />
                                                        </td>
                                                        <td>
                                                            <div className="md-color">
                                                                <input type="color"
                                                                       className="md-color-input"
                                                                       value={this.fieldValue(row, 'font_color', '#000000')}
                                                                       onChange={(e) => this.setField(row.key, 'font_color', e.target.value)}
                                                                       aria-label={`${row.name} 글자 색상`} />
                                                                <span className="md-color-hex">{this.fieldValue(row, 'font_color', '#000000')}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!this.propsError && this.properties.length === 0 && (
                                <p className="md-muted md-search-state">표시 설정 정보가 없습니다.</p>
                            )}

                            {/* 사용자 속성 추가 (인라인 폼) */}
                            <div className="md-add-prop-section">
                                <button type="button" className="md-add-prop-toggle"
                                        onClick={() => this.showAddPropertyForm = !this.showAddPropertyForm}
                                        aria-expanded={this.showAddPropertyForm}>
                                    <FeatherIcon icon={this.showAddPropertyForm ? 'chevron-down' : 'plus'} size="13" aria-hidden="true" />
                                    사용자 속성 추가
                                </button>

                                {this.showAddPropertyForm && (
                                    <div className="md-add-form md-add-form-inline">
                                        <div className="md-add-row">
                                            <label htmlFor="md-new-name">속성 이름 <span className="md-required">*</span></label>
                                            <FormInput id="md-new-name"
                                                       placeholder="예: 베스트 문구"
                                                       value={this.newProperty.name}
                                                       onChange={(e) => this.newProperty.name = e.target.value} />
                                        </div>
                                        <div className="md-add-grid">
                                            <div className="md-add-row">
                                                <label>노출 여부</label>
                                                <FormCheckbox toggle small
                                                              checked={this.newProperty.display === 'T'}
                                                              onChange={() => this.newProperty.display = this.newProperty.display === 'T' ? 'F' : 'T'}>
                                                    {this.newProperty.display === 'T' ? '노출' : '숨김'}
                                                </FormCheckbox>
                                            </div>
                                            <div className="md-add-row">
                                                <label>항목명 노출</label>
                                                <FormCheckbox toggle small
                                                              checked={this.newProperty.display_name === 'T'}
                                                              onChange={() => this.newProperty.display_name = this.newProperty.display_name === 'T' ? 'F' : 'T'}>
                                                    {this.newProperty.display_name === 'T' ? '노출' : '숨김'}
                                                </FormCheckbox>
                                            </div>
                                            <div className="md-add-row">
                                                <label htmlFor="md-new-font-type">글꼴</label>
                                                <FormSelect id="md-new-font-type"
                                                            value={this.newProperty.font_type}
                                                            onChange={(e) => this.newProperty.font_type = e.target.value}>
                                                    {FONT_TYPE_OPTIONS.map(o => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </FormSelect>
                                            </div>
                                            <div className="md-add-row">
                                                <label htmlFor="md-new-font-size">글자 크기</label>
                                                <FormInput id="md-new-font-size" type="number" min="8" max="40"
                                                           value={this.newProperty.font_size}
                                                           onChange={(e) => this.newProperty.font_size = e.target.value} />
                                            </div>
                                            <div className="md-add-row">
                                                <label>글자 색상</label>
                                                <div className="md-color">
                                                    <input type="color"
                                                           className="md-color-input"
                                                           value={this.newProperty.font_color}
                                                           onChange={(e) => this.newProperty.font_color = e.target.value}
                                                           aria-label="새 속성 글자 색상" />
                                                    <span className="md-color-hex">{this.newProperty.font_color}</span>
                                                </div>
                                            </div>
                                            <div className="md-add-row md-add-submit">
                                                <Button theme="primary" size="sm" onClick={() => this.submitAddProperty()}>
                                                    <FeatherIcon icon="plus" size="12" aria-hidden="true" /> 추가하기
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 모달 푸터: 되돌리기/저장 */}
                            <div className="md-props-footer">
                                {this.dirtyKeys.length > 0 ? (
                                    <span className="md-props-dirty">
                                        <FeatherIcon icon="alert-circle" size="13" aria-hidden="true" />
                                        {this.dirtyKeys.length}개 항목 변경됨
                                    </span>
                                ) : <span />}
                                <div>
                                    <Button theme="light" onClick={() => this.closePropsModal()}>닫기</Button>
                                    {this.dirtyKeys.length > 0 && (
                                        <>
                                            <Button className="ml-2" theme="light" onClick={() => this.revertProperties()}>되돌리기</Button>
                                            <Button className="ml-2" theme="primary" onClick={() => this.saveProperties()}>
                                                <FeatherIcon icon="save" size="13" style={{ marginRight: '4px' }} aria-hidden="true" />
                                                변경사항 저장
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                </Modal>

                <ModalLoading isLoading={this.isLoading} />
            </div>
        );
    }
}

export default MainDisplay;
